import { memo, useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { useNote } from 'src/hook/components/useNote';
import { useCountries } from 'src/hook/data/useCountries';
import { useLearning } from 'src/hook/data/useLearning';
import useProgress from 'src/hook/data/useProgress';
import { useSocial } from 'src/hook/data/useSocial';
import { useWordList } from 'src/hook/data/useWordList';
import { useServiceWorker } from 'src/hook/global/useServiceWorker';
import { useTimeZone } from 'src/hook/global/useTimeZone';
import { DarkMode } from 'src/model/enum/enums';
import useUser from '../../hook/data/useUser';
import { useWords } from '../../hook/data/useWords';
import { useWordTranslate } from '../../hook/data/useWordTranslate';
import { initializeTranslations } from '../../i18n';
import Spinner from '../ui/Features/Spinner/Spinner';
import LoadPage from './LoadPage/LoadPage';
import LogOut from './LogOut';
import { RoutesPage } from './RoutesPage';

const Load = () => {
    const [isLoading, setIsLoading] = useState(false);
    const { fetchUserData, userInfo, isUserLoaded, isNonEnglishNativeLanguage } = useUser(false);
    const [searchParams, setSearchParams] = useSearchParams();
    const token = searchParams.get('t');
    const { fetchWords } = useWords(false);
    const { fetchCountries } = useCountries(false);
    const { fetchTranslates } = useWordTranslate(false);
    const { registerWorker } = useServiceWorker();
    const { fetchNoteData } = useNote(false);
    const { fetchSocialInfo } = useSocial(false);
    const { fetchLearningData, learningInfo, isLearningLoaded } = useLearning(false);
    const { fetchAllWordListData } = useWordList();
    const isWorkerRegistered = useRef(false);
    const isAppInitialized = useRef(false);
    const { sendHardwareInfo } = useProgress();
    const { i18n } = useTranslation();
    const { checkTimezone } = useTimeZone();

    const initializeApp = useCallback(async () => {
        if (isLoading) return;
        setIsLoading(true);

        try {
            const [user, learning] =
                await Promise.all([
                    fetchUserData(token!),
                    fetchLearningData(token!),
                ]);

            await Promise.all([
                fetchSocialInfo(user?.UserId!, user?.CountryCode),
                fetchNoteData(user?.UserId),
                fetchWords(user?.RankingsUrl),
                fetchCountries(false),
                fetchTranslates(user?.NativeLanguageCode),
                fetchAllWordListData(learning?.WordLists || []),
            ]);

            // Remove 't' parameter from URL
            searchParams.delete('t');
            setSearchParams(searchParams);

            sendHardwareInfo();

        } catch (error) {
            console.log('error', error);
        } finally {
            setIsLoading(false);
        }
    }, [token, fetchSocialInfo, sendHardwareInfo,
        fetchAllWordListData,
        fetchWords, fetchCountries, fetchTranslates,
        fetchNoteData,
        fetchLearningData, fetchUserData, isLoading, searchParams, setSearchParams]);

    useEffect(() => {
        if (userInfo) {
            if (userInfo.DarkMode) {
                document.body.classList.toggle('dark', userInfo.DarkMode === DarkMode.Dark);
            }
            initializeTranslations(
                userInfo?.NativeLanguageCode, userInfo?.TranslateUI);
        }
    }, [userInfo, isNonEnglishNativeLanguage]);

    useEffect(() => {
        if (token && !isAppInitialized.current) {
            initializeApp();
            isAppInitialized.current = true;
            checkTimezone();
        }
    }, [token, initializeApp, checkTimezone]);

    // Register service worker only once per session
    useEffect(() => {
        if (!isWorkerRegistered.current) {
            registerWorker();
            isWorkerRegistered.current = true;
        }
    }, [registerWorker]);

    if (isLoading) return <LoadPage />;
    else if (!isUserLoaded || !i18n.isInitialized || !isLearningLoaded) return <Spinner fullPage />;
    else if (!token && (!userInfo || !learningInfo)) return <LogOut />;

    else return <RoutesPage />;
};

export default memo(Load);